// Help Centre landing — lists the member portals as cards. Clicking a card
// drills into that portal's existing public experience (/help/:subdomain),
// where the customer picks a request type and raises a ticket that lands in
// THAT portal's own queue.
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHelpCentre } from './HelpCentreLayout';
import { MagnifyingGlass, ArrowRight } from '@phosphor-icons/react';

export default function HelpCentreHomePage() {
  const { portals = [], help_centre: hc } = useHelpCentre();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const theme = hc.branding?.theme_color || '#2563eb';

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return portals;
    return portals.filter(p => `${p.name} ${p.description}`.toLowerCase().includes(s));
  }, [portals, q]);

  const open = (p) => navigate(`/help/${p.subdomain}`);

  return (
    <div data-testid="hc-home-page">
      <div className="relative max-w-2xl mx-auto -mt-16 mb-10">
        <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search portals…"
          className="w-full pl-11 pr-4 py-3.5 rounded-lg border border-zinc-200 shadow-sm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          data-testid="hc-search"
        />
      </div>

      <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-3">Portals</h2>

      {filtered.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="hc-empty">No portals available yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hc-portal-grid">
          {filtered.map(p => (
            <button
              key={p.portal_id}
              onClick={() => open(p)}
              className="group text-left bg-white border border-zinc-200 rounded-xl p-5 hover:shadow-md hover:border-zinc-300 transition-all"
              data-testid={`hc-portal-card-${p.portal_id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                {p.logo_url ? (
                  <img src={p.logo_url} alt="" className="w-11 h-11 rounded-lg object-contain ring-1 ring-zinc-100 bg-zinc-50 p-1" />
                ) : (
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center font-bold text-white" style={{ background: p.theme_color || theme }}>
                    {p.name.charAt(0)}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-900 truncate group-hover:text-blue-600 transition-colors" style={{ color: undefined }}>{p.name}</p>
                  <p className="text-[11px] text-zinc-400">{p.request_type_count} request {p.request_type_count === 1 ? 'type' : 'types'}</p>
                </div>
              </div>
              <p className="text-sm text-zinc-500 line-clamp-2 min-h-[2.5rem]">{p.description || 'Raise a request using the options provided.'}</p>
              <span className="inline-flex items-center gap-1 text-xs font-medium mt-3" style={{ color: theme }}>
                Open portal <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
